import { Breakpoint, Spin, Table } from 'antd'
import { ColumnsType } from 'antd/es/table/interface'
import React, { FunctionComponent, useCallback, useMemo } from 'react'

import {
  AuctionLinkedPropertyRecord,
  AuctionLinkedPropertyTableView,
} from '../../../common/auctionMonitoringTypes'
import { TransformedPropertyLabelData } from '../../../common/propertyDetailTypes'
import { stringSorter } from '../../utils/generalTableHelpers'
import { tablePaginationHiding } from '../../utils/layoutConst'
import { PropertyTableNotes } from '../../utils/propertyDetailNoteHelpers'
import { AuctionMonitoringLinkedPropertyTableAction } from '../AuctionMonitoringLinkedPropertyTableAction/AuctionMonitoringLinkedPropertyTableAction'
import { AuctionMonitoringLinkedPropertyTableSummary } from '../AuctionMonitoringLinkedPropertyTableSummary/AuctionMonitoringLinkedPropertyTableSummary'
import { PropertyDetailLink } from '../PropertyDetailLink/PropertyDetailLink'
import { PropertyNoteModalButton } from '../PropertyNoteModalButton/PropertyNoteModalButton'
import { PropertyTableItemLabel } from '../PropertyTableItemLabel/PropertyTableItemLabel'

export interface AuctionMonitoringLinkedPropertyTableProps {
  auctionId: string
  data: AuctionLinkedPropertyRecord[]
  propertyNotes?: PropertyTableNotes
  propertyLabels?: 'loading' | TransformedPropertyLabelData | null
  viewType: AuctionLinkedPropertyTableView
  activePropertyId?: string
}

const tableId = 'auctionMonitoringLinkedProperty-table'

export const AuctionMonitoringLinkedPropertyTable: FunctionComponent<AuctionMonitoringLinkedPropertyTableProps> =
  props => {
    const columns = useMemo<ColumnsType<AuctionLinkedPropertyRecord>>(
      () => [
        {
          dataIndex: 'id',
          responsive: ['' as Breakpoint],
        },
        {
          title: 'Typ',
          dataIndex: 'propertyType',
          className: 'w-20 first-letter:uppercase',
          sorter: stringSorter('propertyType'),
          responsive:
            props.viewType === 'drawer' ? ['' as Breakpoint] : undefined,
        },
        {
          title: 'Nemovitost',
          dataIndex: 'propertyName',
          className: 'first-letter:uppercase',
          sorter: stringSorter('propertyName'),
          render: (propertyName, record) => (
            <PropertyDetailLink
              text={propertyName}
              propertyId={record.propertyId}
              propertyType={record.propertyType}
              isActive={props.activePropertyId === record.propertyId}
              drawerTarget={props.viewType === 'page'}
            />
          ),
        },
        {
          title: 'Štítky',
          dataIndex: 'id',
          key: 'labels',
          className: 'w-min',
          render: (_, record) =>
            props.propertyLabels === 'loading' ? (
              <Spin size="small" />
            ) : (
              <PropertyTableItemLabel
                editOnly
                propertyId={record.propertyId}
                propertyType={record.propertyType}
                propertyLabel={props.propertyLabels?.[record.propertyId]}
              />
            ),
        },
        {
          title: 'Poznámka',
          dataIndex: 'propertyId',
          key: 'note',
          className: 'w-min',
          render: propertyId => (
            <PropertyNoteModalButton
              note={props.propertyNotes?.[propertyId]}
              className={
                props.viewType === 'drawer' ? 'truncate inline-block w-20' : ''
              }
            />
          ),
        },
        {
          title: 'Akce',
          dataIndex: 'id',
          key: 'actions',
          className: 'w-8',
          render: id => (
            <AuctionMonitoringLinkedPropertyTableAction
              viewType={props.viewType}
              id={id}
            />
          ),
        },
      ],
      [
        props.viewType,
        props.activePropertyId,
        props.propertyLabels,
        props.propertyNotes,
      ]
    )

    const summary = useCallback(
      () => (
        <AuctionMonitoringLinkedPropertyTableSummary
          auctionId={props.auctionId}
          data={props.data}
          viewType={props.viewType}
        />
      ),
      [props.auctionId, props.data, props.viewType]
    )

    return (
      <Table
        id={tableId}
        rowKey="id"
        size="small"
        rowClassName="animate-fadeIn"
        className="mb-4"
        dataSource={props.data}
        columns={columns}
        summary={props.viewType === 'drawer' ? undefined : summary}
        pagination={tablePaginationHiding}
      />
    )
  }
